import { Mail, Trash2, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

export const Panel = ({ title, children, testId, right }) => (
  <section className="bg-zinc-950 border border-zinc-800 rounded-lg p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">{title}</h2>
      {right}
    </div>
    {children}
  </section>
);

export const Kpi = ({ label, value, prev, sub, testId }) => {
  const diff = prev === undefined ? null : value - prev;
  const Trend = diff > 0 ? ArrowUpRight : diff < 0 ? ArrowDownRight : Minus;
  const tone = diff > 0 ? 'text-emerald-400' : diff < 0 ? 'text-red-400' : 'text-zinc-500';
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid={testId}>
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">{label}</p>
      <div className="flex items-baseline gap-2">
        <p className="text-3xl font-bold text-white">{value.toLocaleString()}</p>
        {diff !== null && (
          <span className={`inline-flex items-center text-xs font-mono ${tone}`} data-testid={`${testId}-trend`} title={`Previous 7 days: ${prev}`}>
            <Trend className="w-3.5 h-3.5" />{diff > 0 ? `+${diff}` : diff}
          </span>
        )}
      </div>
      <p className="text-[11px] text-zinc-600 mt-1">{prev !== undefined ? `vs ${prev.toLocaleString()} last week · ` : ''}{sub}</p>
    </div>
  );
};

export const STATUSES = [
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'won', label: 'Won' },
];

export const PipelineChips = ({ pipeline }) => (
  <div className="flex flex-wrap gap-2 mb-3" data-testid="admin-pipeline">
    {STATUSES.map((s) => (
      <span key={s.id} className="text-xs bg-zinc-900 border border-zinc-800 rounded-md px-2.5 py-1 text-zinc-300" data-testid={`pipeline-${s.id}`}>
        {s.label}: <span className={`font-mono ${s.id === 'won' ? 'text-emerald-400' : 'text-brand'}`}>{pipeline?.[s.id] ?? 0}</span>
      </span>
    ))}
  </div>
);

const StatusButtons = ({ value, onChange, id }) => (
  <div role="radiogroup" aria-label="Enquiry status" className="flex gap-1.5 mt-3" data-testid={`admin-status-${id}`}>
    {STATUSES.map((s) => {
      const active = (value || 'new') === s.id;
      return (
        <button key={s.id} type="button" role="radio" aria-checked={active} onClick={() => { if (!active) onChange(s.id); }}
          data-testid={`admin-status-${id}-${s.id}`}
          className={`text-xs px-3 py-1.5 min-h-[36px] rounded-md border transition-colors ${active
            ? (s.id === 'won' ? 'bg-emerald-400 border-emerald-400 text-black' : 'bg-brand border-brand text-black') + ' cursor-default'
            : 'border-zinc-800 text-zinc-400 hover:border-brand/60'}`}>
          {s.label}
        </button>
      );
    })}
  </div>
);

export const DayBars = ({ days }) => {
  const max = Math.max(1, ...days.map((d) => d.count));
  return (
    <div className="flex items-end gap-2 h-32" data-testid="admin-day-bars">
      {days.map((d) => (
        <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
          <span className="text-[10px] font-mono text-zinc-400">{d.count}</span>
          <div className="w-full bg-brand/80 rounded-t transition-all" style={{ height: `${Math.max(3, (d.count / max) * 100)}%` }} />
          <span className="text-[10px] text-zinc-600">{new Date(`${d.day}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short' })}</span>
        </div>
      ))}
    </div>
  );
};

export const Empty = ({ children }) => <p className="text-sm text-zinc-600">{children}</p>;

export const PopularReads = ({ rows }) => (rows.length === 0 ? <Empty>No shared cards opened this week yet.</Empty> : (
  <ol className="space-y-1.5" data-testid="admin-popular-reads">
    {rows.map((r, i) => (
      <li key={r.card_id} className="flex items-center gap-3 text-sm py-1.5 border-b border-zinc-800/50 last:border-0" data-testid={`popular-read-${i}`}>
        <span className="w-5 text-zinc-600 font-mono text-xs">{i + 1}</span>
        <a href={`/r/${r.card_id}`} target="_blank" rel="noopener noreferrer" className="font-mono font-bold hover:text-brand">{r.ticker}</a>
        <span className="text-xs text-zinc-500">{r.market}</span>
        <span className="text-xs text-zinc-400 flex-1 truncate">{r.trade_state}</span>
        <span className="font-mono text-brand">{r.opens}</span>
        <span className="text-[10px] text-zinc-600">opens</span>
      </li>
    ))}
  </ol>
));

export const TopTickers = ({ rows }) => (rows.length === 0 ? <Empty>No analyses this week yet.</Empty> : (
  <ol className="space-y-1.5" data-testid="admin-top-tickers">
    {rows.map((r, i) => (
      <li key={`${r.ticker}-${r.market}`} className="flex items-center gap-3 text-sm py-1.5 border-b border-zinc-800/50 last:border-0">
        <span className="w-5 text-zinc-600 font-mono text-xs">{i + 1}</span>
        <span className="font-mono font-bold">{r.ticker}</span>
        <span className="text-xs text-zinc-500 flex-1">{r.market}</span>
        <span className="font-mono text-zinc-300">{r.count}</span>
      </li>
    ))}
  </ol>
));

export const SOURCE_LABELS = { shared_card: 'Shared card', analysis: 'Analysis page', markets: 'Markets page', tools: 'Tools page', direct: 'Direct' };

export const SourceTag = ({ source, ticker, testId }) => (
  <span className="inline-block whitespace-nowrap text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border border-brand/40 text-brand" data-testid={testId}>
    {SOURCE_LABELS[source] || 'Direct'}{ticker ? ` · ${ticker}` : ''}
  </span>
);

export const LeadSources = ({ sources }) => {
  const entries = Object.entries(sources || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-lead-sources">
      {entries.map(([k, n]) => (
        <span key={k} className="text-xs bg-zinc-900 border border-zinc-800 rounded-md px-2.5 py-1 text-zinc-300">
          {SOURCE_LABELS[k] || k}: <span className="font-mono text-brand">{n}</span>
        </span>
      ))}
    </div>
  );
};

export const MonthlyTable = ({ rows }) => (
  <>
  <div className="sm:hidden space-y-2" data-testid="admin-monthly-cards">
    {rows.map((r) => (
      <div key={r.month} className="border border-zinc-800/60 rounded-md p-3">
        <p className="text-sm text-zinc-300 mb-2">{new Date(`${r.month}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[['Analyses', r.analyses], ['Cards', r.cards_shared], ['Opens', r.card_opens], ['Enquiries', r.enquiries], ['From cards', r.enquiries_from_cards]].map(([l, v]) => (
            <div key={l}><p className={`font-mono ${l === 'Enquiries' && v ? 'text-brand font-bold' : ''}`}>{v}</p><p className="text-[10px] text-zinc-500">{l}</p></div>
          ))}
        </div>
      </div>
    ))}
  </div>
  <div className="hidden sm:block overflow-x-auto" data-testid="admin-monthly-table">
    <table className="w-full text-sm min-w-[520px]">
      <thead>
        <tr className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 text-right">
          <th className="text-left pb-2 font-normal">Month</th>
          <th className="pb-2 font-normal">Analyses</th>
          <th className="pb-2 font-normal">Cards shared</th>
          <th className="pb-2 font-normal">Card opens</th>
          <th className="pb-2 font-normal">Enquiries</th>
          <th className="pb-2 font-normal">From cards</th>
        </tr>
      </thead>
      <tbody className="font-mono text-right">
        {rows.map((r) => (
          <tr key={r.month} className="border-t border-zinc-800/50" data-testid={`monthly-row-${r.month}`}>
            <td className="text-left py-2 font-sans text-zinc-300">{new Date(`${r.month}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</td>
            <td className="py-2">{r.analyses}</td>
            <td className="py-2">{r.cards_shared}</td>
            <td className="py-2">{r.card_opens}</td>
            <td className={`py-2 ${r.enquiries ? 'text-brand font-bold' : ''}`}>{r.enquiries}</td>
            <td className="py-2 text-zinc-400">{r.enquiries_from_cards}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  </>
);

export const EnquiryList = ({ items, onDelete, onStatus }) => (items.length === 0 ? <Empty>No enquiries yet.</Empty> : (
  <div className="space-y-3" data-testid="admin-enquiry-list">
    {items.map((x) => (
      <div key={x.id} className="border border-zinc-800/70 rounded-md p-4" data-testid={`admin-enquiry-${x.id}`}>
        <div className="flex justify-between gap-3 mb-2">
          <div>
            <p className="font-bold text-sm">{x.name}{x.company && <span className="text-zinc-500 font-normal"> · {x.company}</span>}</p>
            <a href={`mailto:${x.email}`} className="text-xs text-brand inline-flex items-center gap-1"><Mail className="w-3 h-3" />{x.email}</a>
            <div className="mt-1.5"><SourceTag source={x.source} ticker={x.source_ticker} testId={`admin-enquiry-source-${x.id}`} /></div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-[11px] text-zinc-600 whitespace-nowrap">{new Date(x.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>
            <button onClick={() => onDelete(x.id)} className="text-zinc-600 hover:text-red-400" aria-label="Delete enquiry" data-testid={`admin-delete-${x.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
        <p className="text-sm text-zinc-300 whitespace-pre-wrap">{x.message}</p>
        <StatusButtons value={x.status} onChange={(s) => onStatus(x.id, s)} id={x.id} />
      </div>
    ))}
  </div>
));
